import {
  type QueryClient,
  type UseQueryResult,
  useQuery,
} from "@tanstack/react-query";
import { useEffect, useState, useSyncExternalStore } from "react";
import { type EventResults, fetchEventResults } from "@/features/events/model";
import { useSupporterSession } from "@/features/supporter/hooks/useSupporterSession";
import {
  getAimgViewerContext,
  subscribeAimgViewerContext,
} from "@/shared/api/client";

const INTEREST_READ = "nijiurachan-events-interest-read-at";
const ORGANIZER_READ = "nijiurachan-events-organizer-read-at";
const READ_EVENT = "nijiurachan-events-read";

export function eventResultsRevision(results: EventResults): string {
  const timestamps = (items: EventResults["interested"]): string[] =>
    items.map((item) => item.judgedAt).sort();
  return JSON.stringify([
    timestamps(results.interested),
    timestamps(results.organized),
  ]);
}

export async function refreshEventOccurrencesBeforeRead(
  client: QueryClient,
  acknowledge: () => void,
): Promise<boolean> {
  try {
    await Promise.all(
      (["calendar", "mine", "detail"] as const).map((scope) =>
        client.invalidateQueries(
          { queryKey: ["events", scope] },
          { throwOnError: true },
        ),
      ),
    );
  } catch {
    return false;
  }
  acknowledge();
  return true;
}

function readTime(key: string): string {
  try {
    return localStorage.getItem(key) ?? "";
  } catch {
    return "";
  }
}
export function markEventsRead(
  organized = false,
  results?: EventResults,
): void {
  const items = organized ? results?.organized : results?.interested;
  if (!items?.length) return;
  try {
    const at = items
      .map((item) => item.judgedAt)
      .sort((left, right) =>
        Temporal.Instant.compare(
          Temporal.Instant.from(left),
          Temporal.Instant.from(right),
        ),
      )
      .at(-1);
    if (!at) return;
    localStorage.setItem(organized ? ORGANIZER_READ : INTEREST_READ, at);
    window.dispatchEvent(new Event(READ_EVENT));
  } catch {
    /* Storage may be unavailable. */
  }
}
export function useEventResults(): UseQueryResult<EventResults, Error> {
  const viewer = useSyncExternalStore(
    subscribeAimgViewerContext,
    getAimgViewerContext,
    getAimgViewerContext,
  );
  const session = useSupporterSession();
  return useQuery({
    queryKey: [
      "events",
      "results",
      viewer,
      session.data?.loggedIn ? session.data.discordUserId : null,
    ],
    queryFn: fetchEventResults,
    refetchInterval: (query) => (query.state.error ? false : 60_000),
    retry: false,
  });
}
export function hasUnreadEventResults(
  results: EventResults | undefined,
  read: { interest: string; organizer: string },
): boolean {
  const newer = (at: string, previous: string): boolean => {
    if (!previous) return true;
    try {
      return (
        Temporal.Instant.compare(
          Temporal.Instant.from(at),
          Temporal.Instant.from(previous),
        ) > 0
      );
    } catch {
      return true;
    }
  };
  return (
    (results?.interested.some((item) => newer(item.judgedAt, read.interest)) ??
      false) ||
    (results?.organized.some((item) => newer(item.judgedAt, read.organizer)) ??
      false)
  );
}
export function useEventStatus(): boolean {
  const query = useEventResults();
  const [read, setRead] = useState(() => ({
    interest: readTime(INTEREST_READ),
    organizer: readTime(ORGANIZER_READ),
  }));
  useEffect(() => {
    const update = (): void =>
      setRead({
        interest: readTime(INTEREST_READ),
        organizer: readTime(ORGANIZER_READ),
      });
    window.addEventListener("storage", update);
    window.addEventListener(READ_EVENT, update);
    return (): void => {
      window.removeEventListener("storage", update);
      window.removeEventListener(READ_EVENT, update);
    };
  }, []);
  return hasUnreadEventResults(query.data, read);
}
